import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';

type JobKind = 'research' | 'position_matrix' | 'country_backfill' | 'investor_type_classify' | 'contact_research' | 'pdf_profile_research';
type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

interface Job {
  id: string;
  kind: JobKind;
  status: JobStatus;
  total: number;
  done: number;
  failed: number;
  current_label: string | null;
  error_msg: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

const KIND_LABELS: Record<JobKind, string> = {
  research: 'Web-Recherche (Modul A)',
  position_matrix: 'Position-Matrix-Anwendung',
  country_backfill: 'Country-Code Backfill (AI)',
  investor_type_classify: 'Investorenart-Klassifikation (AI)',
  contact_research: 'Kontakt-Recherche (AI)',
  pdf_profile_research: 'PDF-Profile-Recherche (Brave + AI)',
};

const STATUS_COLORS: Record<JobStatus, string> = {
  queued: '#888',
  running: '#1565c0',
  done: '#2e7d32',
  failed: '#d32f2f',
  cancelled: '#888',
};

interface ApiStatus { ok: boolean; available: boolean; reason: string; message?: string }

export function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [apiStatus, setApiStatus] = useState<ApiStatus | null>(null);
  const [apiChecking, setApiChecking] = useState(false);

  async function refresh() {
    try {
      const r = await apiClient.get<{ data: Job[] }>('/jobs');
      setJobs(r.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function checkApiStatus() {
    setApiChecking(true);
    try {
      const r = await apiClient.get<ApiStatus>('/settings/anthropic-status');
      setApiStatus(r.data);
    } catch (err) {
      setApiStatus({ ok: false, available: false, reason: 'error', message: (err as Error).message });
    } finally {
      setApiChecking(false);
    }
  }

  useEffect(() => {
    void refresh();
    void checkApiStatus();
    const id = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(id);
  }, []);

  async function startJob(kind: JobKind, payload: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      await apiClient.post('/jobs', { kind, payload });
      await refresh();
    } catch (err) {
      window.alert(`Fehler: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function cancelJob(id: string) {
    if (!window.confirm('Job wirklich abbrechen?')) return;
    await apiClient.post(`/jobs/${id}/cancel`);
    await refresh();
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Hintergrund-Jobs</div>
          <div className="page-header-sub">Bulk-Workflows: Web-Recherche, Klassifikation, Position-Matrix</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 'var(--radius)',
            background: apiStatus?.available ? '#e8f5e9' : apiStatus ? '#fff3e0' : 'var(--surface2)',
            border: `1px solid ${apiStatus?.available ? '#2e7d32' : apiStatus ? '#e65100' : 'var(--border)'}`,
            fontSize: 12, color: apiStatus?.available ? '#2e7d32' : apiStatus ? '#e65100' : 'var(--text3)',
          }}>
            <span style={{ fontWeight: 600 }}>Anthropic API:</span>
            {apiChecking ? '…' : apiStatus?.available ? '✓ verfügbar' : apiStatus?.reason === 'no_credits' ? '⚠ keine Credits' : apiStatus?.reason === 'no_key' ? '⚠ kein API-Key' : apiStatus?.reason === 'auth_error' ? '⚠ Auth-Fehler' : apiStatus ? '⚠ nicht verfügbar' : '…'}
            <button onClick={() => void checkApiStatus()} disabled={apiChecking}
              style={{ marginLeft: 4, padding: '2px 8px', fontSize: 11, background: 'transparent', border: '1px solid currentColor', borderRadius: 4, cursor: 'pointer', color: 'inherit' }}>
              ↻
            </button>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 600 }}>Job starten</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={() => startJob('research', { filter: 'no_contacts' })} disabled={busy}
              style={btnPrimary}>
              Modul A — Web-Recherche für Firmen ohne Kontakte (alle Module)
            </button>
            <button onClick={() => startJob('research', { filter: 'due', recrawl_days: 180 })} disabled={busy}
              style={btnSecondary}>
              Re-Crawl-Scheduler — fällige Firmen (≥ 180 Tage)
            </button>
            <button onClick={() => startJob('country_backfill')} disabled={busy} style={btnSecondary}>
              Country-Code Backfill (AI) — Firmen ohne Land
            </button>
            <button onClick={() => startJob('investor_type_classify')} disabled={busy} style={btnSecondary}>
              Investorenart-Klassifikation (AI) — Firmen ohne Typ
            </button>
            <button onClick={() => startJob('position_matrix')} disabled={busy} style={btnSecondary}>
              Position-Matrix auf alle Bestandskontakte anwenden
            </button>
          </div>
        </div>

        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
            Letzte Jobs
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Laden…</div>
          ) : jobs.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Noch keine Jobs.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface2)', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase' }}>
                  <th style={th}>Art</th>
                  <th style={th}>Status</th>
                  <th style={th}>Fortschritt</th>
                  <th style={th}>Aktuell</th>
                  <th style={th}>Gestartet</th>
                  <th style={th}> </th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => {
                  const pct = j.total > 0 ? Math.round(((j.done + j.failed) / j.total) * 100) : 0;
                  return (
                    <tr key={j.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={td}>{KIND_LABELS[j.kind] ?? j.kind}</td>
                      <td style={{ ...td, color: STATUS_COLORS[j.status], fontWeight: 600 }}>{j.status}</td>
                      <td style={td}>
                        {j.done + j.failed} / {j.total} ({pct}%)
                        {j.failed > 0 && <span style={{ color: '#d32f2f', marginLeft: 6 }}>· {j.failed} fail</span>}
                      </td>
                      <td style={{ ...td, color: 'var(--text3)' }}>{j.current_label ?? '—'}</td>
                      <td style={{ ...td, color: 'var(--text3)', fontSize: 11 }}>
                        {j.started_at ? new Date(j.started_at).toLocaleString('de-DE') : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {(j.status === 'queued' || j.status === 'running') && (
                          <button onClick={() => cancelJob(j.id)} style={btnLink}>Abbrechen</button>
                        )}
                        {j.status === 'failed' && j.error_msg && (
                          <span title={j.error_msg} style={{ color: '#d32f2f', fontSize: 11 }}>Fehler ⓘ</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left',
};
const btnSecondary: React.CSSProperties = {
  padding: '8px 14px', background: 'var(--surface)', color: 'var(--text)',
  border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, textAlign: 'left',
};
const btnLink: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12,
};
const th: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontWeight: 500 };
const td: React.CSSProperties = { padding: '10px 12px' };
